import { useQuery } from '@tanstack/react-query';
import { hodTeamAPI } from '../services/api';

const FIVE_MINUTES = 5 * 60 * 1000;
const THREE_MINUTES = 3 * 60 * 1000;
const TEN_MINUTES = 10 * 60 * 1000;

const retryFn = (failureCount, error) => {
  const s = error?.response?.status;
  if (s === 403 || s === 404) return false;
  return failureCount < 2;
};

// ---------------------------------------------------------------------------
// Overview — department snapshot
// ---------------------------------------------------------------------------

export const useHODTeamOverview = (date, { enabled = true } = {}) => {
  return useQuery({
    queryKey: ['hod-team', 'overview', date ?? 'today'],
    queryFn: async () => {
      const response = await hodTeamAPI.getOverview(date);
      return response.data;
    },
    staleTime: THREE_MINUTES,
    refetchInterval: enabled ? FIVE_MINUTES : false,
    retry: retryFn,
    enabled,
  });
};

// ---------------------------------------------------------------------------
// Trends — weekly aggregated patterns (department scope)
// ---------------------------------------------------------------------------

export const useHODTeamTrends = (weeks = 8, { enabled = true } = {}) => {
  return useQuery({
    queryKey: ['hod-team', 'trends', weeks],
    queryFn: async () => {
      const response = await hodTeamAPI.getTrends(weeks);
      return response.data;
    },
    staleTime: TEN_MINUTES,
    retry: retryFn,
    enabled,
  });
};

// ---------------------------------------------------------------------------
// Leave insights — department-level leave analysis
// ---------------------------------------------------------------------------

export const useHODTeamLeaveInsights = (year, { enabled = true } = {}) => {
  return useQuery({
    queryKey: ['hod-team', 'leave-insights', year ?? 'current'],
    queryFn: async () => {
      const response = await hodTeamAPI.getLeaveInsights(year);
      return response.data;
    },
    staleTime: TEN_MINUTES,
    retry: retryFn,
    enabled,
  });
};

// ---------------------------------------------------------------------------
// Individual detail — per-employee monthly view (department scope)
// ---------------------------------------------------------------------------

export const useHODTeamIndividual = (staffId, month) => {
  return useQuery({
    queryKey: ['hod-team', 'individual', staffId, month ?? 'current'],
    queryFn: async () => {
      const response = await hodTeamAPI.getIndividualDetail(staffId, month);
      return response.data;
    },
    staleTime: FIVE_MINUTES,
    retry: retryFn,
    enabled: Boolean(staffId),
  });
};

// ---------------------------------------------------------------------------
// RO Breakdown — sub-team comparison grouped by Reporting Officer
// ---------------------------------------------------------------------------

export const useHODRoBreakdown = () => {
  return useQuery({
    queryKey: ['hod-team', 'ro-breakdown'],
    queryFn: async () => {
      const response = await hodTeamAPI.getRoBreakdown();
      return response.data;
    },
    staleTime: FIVE_MINUTES,
    retry: retryFn,
  });
};

// ---------------------------------------------------------------------------
// Upcoming leave — next N days
// ---------------------------------------------------------------------------

export const useHODTeamUpcomingLeave = (days = 7) => {
  return useQuery({
    queryKey: ['hod-team', 'upcoming-leave', days],
    queryFn: async () => {
      const response = await hodTeamAPI.getUpcomingLeave(days);
      return response.data;
    },
    staleTime: TEN_MINUTES,
    retry: retryFn,
  });
};
